import React, { useEffect, useRef, useState } from 'react';

import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteRevenueCategories from 'hooks/revenueCategory/useAutoCompleteRevenueCategories';

import SelectorButton from './SelectorButton';

export interface RevenueCategoryAutoCompleteValue extends AutocompleteItem {
  revenueCategoryId: string;
  label: string;
}

interface ICategoryAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: RevenueCategoryAutoCompleteValue;
  disinclude?: Array<string>;
  onChange: (item: RevenueCategoryAutoCompleteValue | null) => void;
}

const RevenueCategoryAutoComplete = ({
  value,
  onChange,
  disinclude = [],
  ...autocompleteProps
}: ICategoryAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteRevenueCategories({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: RevenueCategoryAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data
          .filter(category => !disinclude.includes(category.revenueCategoryId))
          .map(category => ({
            ...category,
            value: category.revenueCategoryId,
            label: category.name,
          })),
      );
    }
  }, [isSuccess, data, disinclude]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      key={value.revenueCategoryId}
      placeholder='Select category'
      {...autocompleteProps}
    />
  );
};

export default RevenueCategoryAutoComplete;
